// Shared builder for the TickerDetail `tradeContext`.
//
// Both entry points into the ticker chart must produce the SAME window:
//   • BacktestDetail → click on a ticker in the trades table
//   • StrategyAnalyzer → direct URL / page reload on /analyzer/:id/:ticker
// Before this helper the URL path anchored the loaded window to the trades
// themselves (first trade − 5d), so a chart opened by link started right at the
// BUY bar — no history to judge WHY the entry fired (dip filter, peak window…).

export const HALF_YEAR_DAYS = 183;

// Trading bars per calendar trading day, per data type.
export const BARS_PER_DAY = { daily: 1, hourly: 6.5, '5m': 78, '1m': 390 };

// JS mirror of _warmup_bars() in web/backend/routers/backtesting.py — the largest
// lookback (in bars) any filter of the strategy reaches back. Used as a fallback
// when the API response carries no _meta.warmup_bars (e.g. additional-period rows).
export function warmupBarsFromParams(params = {}) {
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  };
  return Math.max(
    0,
    num(params.buy_window_days), num(params.t_hours_buy), num(params.t_buy), num(params.t_days),
    num(params.sell_window_days),
    num(params.dip_period_bars),
    num(params.overpriced_period_bars),
  );
}

// Calendar days of pre-history to load/show before the entry.
// = the strategy's own lookback (dip bars & co, over-shot 2× for weekends and
//   holidays), but never less than half a year.
export function preHistoryDays(params = {}, warmupBars = 0) {
  const bpd = BARS_PER_DAY[params.data_type || 'hourly'] ?? 6.5;
  const wb  = warmupBars > 0 ? warmupBars : warmupBarsFromParams(params);
  const warmupDays = wb > 0 ? Math.ceil((wb / bpd) * 2) : 0;
  return Math.max(HALF_YEAR_DAYS, warmupDays);
}

// Half a year expressed in bars of the given data type (~130 trading days).
export function halfYearBars(dataType = 'hourly') {
  const bpd = BARS_PER_DAY[dataType] ?? 6.5;
  return Math.round(bpd * 130);
}

const isoDay = (d) => d.toISOString().split('T')[0];

/**
 * Build the tradeContext consumed by <TickerDetail tradeContext=… />.
 *
 * Window: [min(period start, first BUY) − preHistory  …  period end].
 * The "Угода" preset zooms to the trade bracket inside that loaded window.
 *
 * @param {object}   o
 * @param {string}   o.ticker
 * @param {object[]} o.trades      — trades of THIS ticker only
 * @param {object}   o.params      — strategy params of the backtest row
 * @param {object}   [o.meta]      — API `_meta` (universe/filter context for the label)
 * @param {number}   [o.warmupBars]— from the API `_meta.warmup_bars` when available
 * @param {string}   [o.dataType]  — override (period details may differ)
 */
export function buildTickerTradeContext({ ticker, trades = [], params = {}, meta, warmupBars, dataType }) {
  const byTime = (a, b) => new Date(a.time) - new Date(b.time);
  const valid  = trades.filter(t => t?.time && !isNaN(new Date(t.time).getTime()));
  const buys   = valid.filter(t => t.action === 'BUY').sort(byTime);
  const sells  = valid.filter(t => t.action === 'SELL').sort(byTime);
  const sorted = [...valid].sort(byTime);

  const wb = warmupBars != null && warmupBars > 0 ? warmupBars : warmupBarsFromParams(params);

  // Left anchor: the backtest period start, or — when it starts later than the
  // period (or the row has no period at all) — the first trade.
  const firstTradeT = sorted[0] ? new Date(sorted[0].time) : new Date();
  const periodStart = params.start_time ? new Date(params.start_time) : firstTradeT;
  const leftAnchor  = new Date(Math.min(periodStart.getTime(), firstTradeT.getTime()));

  const lastTradeT = sorted.length ? new Date(sorted[sorted.length - 1].time) : new Date();
  const periodEnd  = params.stop_time ? new Date(params.stop_time) : lastTradeT;
  const rightAnchor = new Date(Math.max(periodEnd.getTime(), lastTradeT.getTime()));

  const fromDate = new Date(leftAnchor);
  fromDate.setDate(fromDate.getDate() - preHistoryDays(params, wb));
  const toDate = new Date(rightAnchor);
  toDate.setDate(toDate.getDate() + 5);   // small right pad so the last marker isn't clipped

  return {
    ticker,
    allTickerTrades: trades,
    fromDate:        isoDay(fromDate),
    toDate:          isoDay(toDate),
    dataType:        dataType || params.data_type || 'daily',
    // Some universe/filter fields live only in _meta — merge so the params label
    // on the ticker page shows the same filter block as the backtest detail page.
    strategyParams:  meta ? { ...params, ...meta } : params,
    warmupBars:      wb,
    buyTrade:  buys[0]                 || null,
    sellTrade: sells[sells.length - 1] || null,
  };
}
